import { Button, InputNumber, Segmented, Select, Switch, message } from "antd";
import { apiV2Root } from "consts";
import bem from "easy-bem";
import React, { useState } from "react";

import {
  DEFAULT_SETTINGS,
  ExportFormat,
  ExportKind,
  ExportOrderField,
  ExportSettings,
  MAX_DESC_LEN,
  MAX_LIMIT,
  ORDER_FIELD_LABELS,
  buildExportQuery,
  exportFileName,
} from "./utils";

import "./CasesExport.css";

const cn = bem("cases-export");

const ORDER_OPTIONS = (
  Object.keys(ORDER_FIELD_LABELS) as ExportOrderField[]
).map((value) => ({ value, label: ORDER_FIELD_LABELS[value] }));

// Скачивает blob под заданным именем через временную ссылку.
const saveBlob = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

// Вкладка «Экспорт» в профиле модератора: форма настроек и кнопка скачивания
// выгрузки кейсов (CSV/TSV). Настройки — локальное состояние, в URL не пишем.
const CasesExport: React.FC = () => {
  const [settings, setSettings] = useState<ExportSettings>(DEFAULT_SETTINGS);
  const [isDownloading, setIsDownloading] = useState(false);

  const update = <K extends keyof ExportSettings>(
    key: K,
    value: ExportSettings[K],
  ) => setSettings((prev) => ({ ...prev, [key]: value }));

  const handleDownload = async () => {
    if (isDownloading) return;

    setIsDownloading(true);
    try {
      // fetch, а не переход по ссылке: 403 и JSON 400 покажем сообщением,
      // а не уведём страницу на JSON.
      const response = await fetch(
        `${apiV2Root}moderatorial/export/cases/?${buildExportQuery(settings)}`,
        { credentials: "include" },
      );

      if (!response.ok) {
        let detail = "";
        try {
          detail = (await response.json()).detail ?? "";
        } catch {
          // тело не JSON — оставляем общий текст
        }
        throw new Error(
          detail || `Не удалось скачать выгрузку (${response.status})`,
        );
      }

      saveBlob(await response.blob(), exportFileName(settings.format));
      message.success("Выгрузка скачана");
    } catch (error) {
      message.error(
        error instanceof Error ? error.message : "Ошибка скачивания выгрузки",
      );
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className={cn()}>
      <h3 className={cn("title")}>Выгрузка кейсов</h3>

      <div className={cn("field")}>
        <label className={cn("label")}>Что выгружать</label>
        <Segmented<ExportKind>
          value={settings.kind}
          options={[
            { value: "all", label: "Всё" },
            { value: "problem", label: "Проблемы" },
            { value: "solution", label: "Решения" },
          ]}
          onChange={(value) => update("kind", value)}
        />
      </div>

      <div className={cn("field")}>
        <label className={cn("label")}>Сортировка</label>
        <div className={cn("row")}>
          <Select<ExportOrderField>
            className={cn("select")}
            value={settings.orderField}
            options={ORDER_OPTIONS}
            onChange={(value) => update("orderField", value)}
          />
          <Segmented<string>
            value={settings.descending ? "desc" : "asc"}
            options={[
              { value: "desc", label: "По убыванию" },
              { value: "asc", label: "По возрастанию" },
            ]}
            onChange={(value) => update("descending", value === "desc")}
          />
        </div>
      </div>

      <div className={cn("field")}>
        <label className={cn("label")}>Формат</label>
        <Segmented<ExportFormat>
          value={settings.format}
          options={[
            { value: "csv", label: "CSV" },
            { value: "tsv", label: "TSV" },
          ]}
          onChange={(value) => update("format", value)}
        />
      </div>

      <div className={cn("field")}>
        <label className={cn("label")}>Длина описания (символов)</label>
        <InputNumber
          min={0}
          max={MAX_DESC_LEN}
          precision={0}
          value={settings.descLen}
          onChange={(value) =>
            update("descLen", value ?? DEFAULT_SETTINGS.descLen)
          }
        />
      </div>

      <div className={cn("field")}>
        <label className={cn("label")}>Колонки по сферам</label>
        <Switch
          checked={settings.spheres}
          onChange={(value) => update("spheres", value)}
        />
      </div>

      <div className={cn("field")}>
        <label className={cn("label")}>Колонки по типам атрибутов</label>
        <Switch
          checked={settings.attrs}
          onChange={(value) => update("attrs", value)}
        />
      </div>

      <div className={cn("field")}>
        <label className={cn("label")}>Максимум строк</label>
        <InputNumber
          min={1}
          max={MAX_LIMIT}
          precision={0}
          value={settings.limit}
          onChange={(value) => update("limit", value ?? DEFAULT_SETTINGS.limit)}
        />
        <span className={cn("hint")}>Больше {MAX_LIMIT} — пока нельзя</span>
      </div>

      <Button
        type="primary"
        loading={isDownloading}
        onClick={handleDownload}
      >
        Скачать
      </Button>
    </div>
  );
};

export default CasesExport;
